/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import "./CopyButton.css";
import PropTypes from "prop-types";
import { v2 as Logger, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";

const COMPONENT_NAME = "CCP components/CopyButton";

/**
 * This component is used to copy a given value to the clipboard.
 * @param {string} value The string value to be copied to clipboard.
 */
export const CopyButton = ({ value, buttonText, title = "copy", style }) => {
	const [copied, setCopied] = React.useState(false);

	function fallbackCopyTextToClipboard(text) {
		const textArea = document.createElement("textarea");
		textArea.value = text;

		// Avoid scrolling to bottom
		textArea.style.top = "0";
		textArea.style.left = "0";
		textArea.style.position = "fixed";

		document.body.appendChild(textArea);
		textArea.focus();
		textArea.select();

		try {
			const successful = document.execCommand("copy");
			if (successful) {
				setCopied(true);
				setTimeout(() => setCopied(false), 1500);
			}
		} catch (error) {
			Logger.error("Error when copying using fallback option", {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.COPY_FALLBACK_ERROR,
			});
		}

		document.body.removeChild(textArea);
	}

	function copyTextToClipboard(text) {
		if (!navigator.clipboard) {
			fallbackCopyTextToClipboard(text);
			return;
		}

		navigator.clipboard.writeText(text).then(
			function () {
				setCopied(true);
				setTimeout(() => setCopied(false), 1500);
			},
			function (error) {
				Logger.error("Error when copying", {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.COPY_ERROR,
				});
			},
		);
	}

	return (
		<span onClick={() => copyTextToClipboard(value)}>
			{copied ? (
				<i className="fas fa-check fa-sm attribute-value-copy-button" style={style}></i>
			) : buttonText ? (
				<div title={title} style={style}>
					{buttonText}
				</div>
			) : (
				<i className="fas fa-copy fa-sm attribute-value-copy-button" style={style} title={title}></i>
			)}
		</span>
	);
};

CopyButton.propTypes = {
	value: PropTypes.string.isRequired,
};
